import { pointer, select, selection } from 'd3-selection';
import 'd3-transition';

const clipboardPath = `M310.634,43.714H297.17c-7.194,0-13.026-5.833-13.026-13.026V30.25C284.144,13.543,270.6,0,253.893,0h-0.699
c-16.707,0-30.25,13.543-30.25,30.25v0.438c0,7.194-5.833,13.027-13.026,13.027h-13.464c-14.341,0-25.966,11.625-25.966,25.966
v0.525c0,14.341,11.625,25.966,25.966,25.966h114.183c14.34,0,25.965-11.626,25.965-25.966V69.68
C336.601,55.34,324.976,43.714,310.634,43.714z M253.543,43.714c-7.243,0-13.114-5.872-13.114-13.114
c0-7.243,5.872-13.115,13.114-13.115c7.243,0,13.114,5.872,13.114,13.115C266.657,37.843,260.786,43.714,253.543,43.714z
 M311.929,550.8H91.8c-19.314,0-34.971-15.657-34.971-34.971V96.171c0-19.314,15.657-34.971,26.228-34.971h70.854
c-0.591,2.904-0.91,5.914-0.91,9.008c0,23.958,19.492,43.45,43.454,43.45h114.178c23.963,0,43.453-19.492,43.453-43.979
c0-2.903-0.311-5.733-0.855-8.479h53.312c28.059,0,43.715,15.657,43.715,34.971v262.728c-2.906-0.181-5.791-0.442-8.742-0.442
c-12.094,0-23.771,1.704-34.973,4.604V157.371H100.543v349.715h201.528C303.029,522.453,306.387,537.162,311.929,550.8z
 M441.515,384.686c-62.771,0-113.656,50.887-113.656,113.657S378.743,612,441.515,612s113.656-50.887,113.656-113.657
S504.286,384.686,441.515,384.686z M515.782,474.991l-69.943,78.686c-3.445,3.877-8.244,5.865-13.076,5.865
c-3.838,0-7.697-1.255-10.916-3.833l-43.715-34.971c-7.539-6.028-8.764-17.034-2.727-24.573c6.027-7.547,17.037-8.769,24.576-2.731
l30.748,24.598l58.916-66.28c6.408-7.215,17.465-7.872,24.684-1.451C521.55,456.721,522.198,467.769,515.782,474.991z M362.829,306
H135.515c-4.829,0-8.743-3.915-8.743-8.743v-8.743c0-4.829,3.915-8.743,8.743-8.743h227.314c4.828,0,8.744,3.915,8.744,8.743v8.743
C371.571,302.085,367.657,306,362.829,306z M362.829,227.314H135.515c-4.829,0-8.743-3.915-8.743-8.743v-8.743
c0-4.829,3.915-8.743,8.743-8.743h227.314c4.828,0,8.744,3.915,8.744,8.743v8.743C371.571,223.4,367.657,227.314,362.829,227.314z
 M319.114,375.943h-183.6c-4.829,0-8.743-3.915-8.743-8.743v-8.743c0-4.829,3.915-8.743,8.743-8.743h183.6
c4.83,0,8.744,3.914,8.744,8.743v8.743C327.856,372.028,323.944,375.943,319.114,375.943z M301.629,454.629H135.515
c-4.829,0-8.743-3.915-8.743-8.743v-8.743c0-4.829,3.915-8.743,8.743-8.743h166.115c4.829,0,8.743,3.914,8.743,8.743v8.743
C310.372,450.714,306.458,454.629,301.629,454.629z`;

// From https://stackoverflow.com/questions/400212/how-do-i-copy-to-the-clipboard-in-javascript
//
// Copies a string to the clipboard. Must be called from within an
// event handler such as click. May return false if it failed, but
// this is not always possible. Browser support for Chrome 43+,
// Firefox 42+, Safari 10+, Edge and Internet Explorer 10+.
// Internet Explorer: The clipboard feature may be disabled by
// an administrator. By default a prompt is shown the first
// time the clipboard is used (per session).
export function copyToClipboard(text) {
  if (window.clipboardData && window.clipboardData.setData) {
    // Internet Explorer-specific code path to prevent textarea being shown while dialog is visible.
    return window.clipboardData.setData('Text', text);
  }
  if (document.queryCommandSupported && document.queryCommandSupported("copy")) {
    const textarea = document.createElement('textarea');
    textarea.textContent = text;
    textarea.style.position = 'fixed';  // Prevent scrolling to bottom of page in Microsoft Edge.
    document.body.appendChild(textarea);
    textarea.select();
    try {
      return document.execCommand('copy');  // Security exception may be thrown by some browsers.
    } catch (error) {
      console.warn('Copy to clipboard failed.', error);
      return false;
    } finally {
      document.body.removeChild(textarea);
    }
  }
}

export function exposeToClipboard(selected) {
  if (!(selected instanceof selection)) {
    // this is not a D3JS selection, rather a DOM node
    selected = select(selected);
  }
  selected.on('mousedown.copy', (event) => select(event.target).style('cursor', 'grabbing'));
  selected.on('mouseup.copy', (event) => select(event.target).style('cursor', null));
  selected.on('click.copy', (event) => {
    const element = select(event.target);
    const node = element.node();
    let text;
    let svg = node.nodeName === 'svg' ? element : select(element.ownerSVGElement);
    if (svg && !svg.empty()) {
      // This is already a SVG element
      const title = element.select('title');
      if (!title || title.empty()) {
        return;
      }
      text = title.text();
    } else {
      // Adding a SVG snack to non-SVG elements is still a work in progress...
      // This does not work as it is.
      text = node.value || element.attr('value') || element.text();
      if (!text) {
        return;
      }
      svg = select(selected.node().parentElement).append('svg')
        .attr('width', '200')
        .attr('height', '200')
        .attr('viewBox', '0, 0, 1000, 1000')
        .attr('class', 'clipboard-snack')
        .style('position', 'absolute')
        .style('top', '-100px');
    }
    console.log(text);
    const copied = copyToClipboard(text);
    if (svg.classed('clipboard-snack')) {
      // Adding a SVG snack to non-DVG elements does not work yet.
      // Let's just bail out here.
      svg.remove();
      return;
      // Could also just show a text notification...
    }
    if (!copied) {
      return;
    }

    const mouseCoords = pointer(node);
    const snack = svg.selectAll('g.snack').data([1]).join('g')
      .attr('class', 'snack')
      .attr('transform', `translate(${mouseCoords[0] -10},${mouseCoords[1] - 2}) scale(0.03)`)
      .style('shape-rendering', 'auto')
      .style('opacity', 0.85);
    snack.append('path')
      .style('stroke', 'black')
      .style('stroke', 'black')
      .attr('d', clipboardPath);
    snack.transition()
      .delay(750)
      .duration(250)
      .style('opacity', 0)
      .on('end', () => {
        snack.remove();
        if (svg.classed('clipboard-snack')) {
          svg.remove();
        }
      });
  });
}

export function removeClipboardListener(selected) {
  if (!(selected instanceof selection)) {
    // this is not a D3JS selection, rather a DOM node
    selected = select(selected);
  }
  selected.on('mousedown.copy', null);
  selected.on('mouseup.copy', null);
  selected.on('click.copy', null);
}
